import { useState, useEffect } from "react";
import { Input } from "@/components/tailwind/input.tsx";
import { Button } from "@/components/ui/button";
import { useAdminTranslation } from "@/i18n/hooks";
import { useAuth } from "@/context/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Text } from "@/components/tailwind/text.tsx";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Cake, Pencil, Check } from "lucide-react";
import ValidationAlert from "@/components/validation-alert.tsx";
import { getAgeHighlightThreshold, updateAgeHighlightThreshold } from "@/services/birthday-settings-services";
import log from '@/lib/logger'


export default function SectionBirthdaySettings() {
    const { t } = useAdminTranslation();
    const { token } = useAuth();
    const { toast } = useToast();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [threshold, setThreshold] = useState<number>(70);
    const [isEditing, setIsEditing] = useState(false);
    const [newThreshold, setNewThreshold] = useState<string>("");
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        loadCurrentThreshold();
    }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

    async function loadCurrentThreshold() {
        if (!token) return;

        setIsLoading(true);
        try {
            const thresholdValue = await getAgeHighlightThreshold(token);
            setThreshold(thresholdValue);
        } catch (error) {
            log.error("Error fetching age threshold:", error);
            // Use default value on network/parse errors
            setThreshold(70);
        } finally {
            setIsLoading(false);
        }
    }

    function startEditing() {
        setNewThreshold(threshold.toString());
        setIsEditing(true);
        setValidationError(null);
    }

    function cancelEditing() {
        setIsEditing(false);
        setValidationError(null);
    }

    function validateThreshold(value: string): boolean {
        const numValue = parseInt(value, 10);
        if (!value || isNaN(numValue)) {
            setValidationError(t('adminSettings.birthdaySettings.validation.required'));
            return false;
        }
        if (!Number.isInteger(numValue)) {
            setValidationError(t('adminSettings.birthdaySettings.validation.integer'));
            return false;
        }
        if (numValue < 50) {
            setValidationError(t('adminSettings.birthdaySettings.validation.min'));
            return false;
        }
        if (numValue > 100) {
            setValidationError(t('adminSettings.birthdaySettings.validation.max'));
            return false;
        }
        setValidationError(null);
        return true;
    }

    async function saveNewThreshold() {
        if (!validateThreshold(newThreshold)) return;

        const thresholdValue = parseInt(newThreshold, 10);

        setIsSaving(true);
        try {
            await updateAgeHighlightThreshold(token!, thresholdValue);

            toast({
                title: t('adminSettings.birthdaySettings.toast.success.title'),
                description: t('adminSettings.birthdaySettings.toast.success.description'),
                variant: "default"
            });

            setIsEditing(false);
            loadCurrentThreshold();
        } catch (_error) {
            toast({
                title: t('adminSettings.birthdaySettings.toast.error.title'),
                description: t('adminSettings.birthdaySettings.toast.error.description'),
                variant: "destructive"
            });
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <Card className="p-6 mt-6 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle className="text-xl font-bold flex items-center gap-2">
                        <Cake className="h-5 w-5" />
                        {t('adminSettings.birthdaySettings.title')}
                    </CardTitle>

                    {!isEditing && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={startEditing}
                            className="flex items-center"
                        >
                            <Pencil className="h-4 w-4 mr-1"/>
                            {t('adminSettings.mileageRate.button.edit.text.update')}
                        </Button>
                    )}
                </div>
            </CardHeader>

            <CardContent>
                {isLoading ? (
                    <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
                        {t('adminSettings.mileageRate.loading')}
                    </div>
                ) : isEditing ? (
                    <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                            <Input
                                type="number"
                                min="50"
                                max="100"
                                value={newThreshold}
                                onChange={(e) => setNewThreshold(e.target.value)}
                                className="w-20"
                                invalid={!!validationError}
                                placeholder="70"
                                autoFocus
                            />
                            <Text>{t('adminSettings.birthdaySettings.ageThreshold.unit')}</Text>
                        </div>

                        {validationError && (
                            <ValidationAlert message={validationError}/>
                        )}

                        <div className="flex space-x-2">
                            <Button
                                size="sm"
                                onClick={saveNewThreshold}
                                disabled={isSaving}
                                className="flex items-center"
                            >
                                <Check className="h-4 w-4 mr-1"/>
                                {isSaving
                                    ? t('adminSettings.mileageRate.button.save.text.saving')
                                    : t('adminSettings.mileageRate.button.save.text.save')}
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={cancelEditing}
                                disabled={isSaving}
                            >
                                {t('adminSettings.mileageRate.button.cancel.text')}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div>
                        <div className="flex items-baseline mb-1">
                            <span className="text-2xl font-medium mr-2">
                                {threshold}
                            </span>
                            <Text className="text-sm">
                                {t('adminSettings.birthdaySettings.ageThreshold.unit')}
                            </Text>
                        </div>

                        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                            <p>
                                {t('adminSettings.birthdaySettings.description')}
                            </p>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}